import React, { useState } from 'react';
import { Mail, Phone, MapPin, MessageCircle, Send, CheckCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/forms/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Message Sent! ✅",
          description: data.message || "Thank you for contacting HVGTEC. We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        throw new Error(data.detail || 'Submission failed');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Submission Error",
        description: "There was a problem sending your message. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Contact <span className="text-cyan-400">HVGTEC</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Get in touch with our team for franchise inquiries, technical support, or general questions
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">Get in Touch</h2>
                <p className="text-gray-400 leading-relaxed">
                  Whether you're interested in franchise opportunities, need technical support, or have questions about our services, we're here to help.
                </p>
              </div>

              {/* Contact Methods */}
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-cyan-400" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Location</h3>
                    <p className="text-gray-400 text-sm">{companyInfo.location}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-cyan-400" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email</h3>
                    <a 
                      href={`mailto:${companyInfo.email}`}
                      className="text-gray-400 text-sm hover:text-cyan-400 transition-colors"
                    >
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="text-cyan-400" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Phone</h3>
                    <a 
                      href={`tel:${companyInfo.phone}`}
                      className="text-gray-400 text-sm hover:text-cyan-400 transition-colors"
                    >
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="text-cyan-400" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Instant Messaging</h3>
                    <div className="space-y-1">
                      <a 
                        href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-gray-400 text-sm hover:text-cyan-400 transition-colors"
                      >
                        WhatsApp: {companyInfo.whatsapp}
                      </a>
                      <a 
                        href={companyInfo.telegram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-gray-400 text-sm hover:text-cyan-400 transition-colors"
                      >
                        Telegram Support
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-white font-semibold mb-4">Business Hours</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Monday - Saturday</span>
                    <span className="text-cyan-400">8:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Sunday</span>
                    <span className="text-gray-400">Closed</span>
                  </div>
                  <div className="pt-2 mt-2 border-t border-slate-700">
                    <span className="text-cyan-400 text-xs">24/7 Technical Support Available</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-white mb-6">Send us a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-white font-medium mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="bg-slate-800 border-slate-600 text-white focus:border-cyan-500"
                        placeholder="Juan Dela Cruz"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-white font-medium mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="bg-slate-800 border-slate-600 text-white focus:border-cyan-500"
                        placeholder="juan@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-white font-medium mb-2">
                        Phone Number
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="bg-slate-800 border-slate-600 text-white focus:border-cyan-500"
                        placeholder="+63 912 345 6789"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-white font-medium mb-2">
                        Subject *
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="bg-slate-800 border-slate-600 text-white focus:border-cyan-500"
                        placeholder="Franchise Inquiry"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-white font-medium mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className="bg-slate-800 border-slate-600 text-white focus:border-cyan-500 resize-none"
                      placeholder="Tell us about your requirements or questions..."
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-6 text-lg rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2" size={20} />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Looking for Something Specific?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center hover:border-cyan-500/50 transition-all duration-300">
              <div className="w-16 h-16 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Franchise Opportunities</h3>
              <p className="text-gray-400 text-sm mb-4">
                Learn about our franchise models and ROI projections
              </p>
              <a href="/franchise" className="text-cyan-400 hover:text-cyan-300 text-sm font-medium">
                View Franchise Options →
              </a>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center hover:border-cyan-500/50 transition-all duration-300">
              <div className="w-16 h-16 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Phone className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Technical Support</h3>
              <p className="text-gray-400 text-sm mb-4">
                24/7 support for existing deployments and partners
              </p>
              <a href={`tel:${companyInfo.phone}`} className="text-cyan-400 hover:text-cyan-300 text-sm font-medium">
                Call Support →
              </a>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center hover:border-cyan-500/50 transition-all duration-300">
              <div className="w-16 h-16 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Instant Chat</h3>
              <p className="text-gray-400 text-sm mb-4">
                Connect with us on WhatsApp or Telegram for quick inquiries
              </p>
              <a 
                href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 text-sm font-medium"
              >
                Chat Now →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
